import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';
import { createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IKpiPresetWeightVehicle } from 'app/shared/model/kpi-preset-weight-vehicle.model';
import { IKpiConfWeight } from 'app/shared/model/kpi-conf-weight.model';
import { IKpiPresetWeight } from 'app/shared/model/kpi-preset-weight.model';
import { cleanEntity } from 'app/shared/util/entity-utils';

export interface IPresetWeightState extends EntityState<IKpiPresetWeight> {
  loading: boolean;
  errorMessage: string | null;
  kpiConfWeight: IKpiConfWeight[];
  kpiWeightVehicles: IKpiPresetWeightVehicle[];
  updating: boolean;
  updateSuccess: boolean;
}

const initialState: IPresetWeightState = {
  loading: false,
  errorMessage: null,
  kpiConfWeight: [],
  kpiWeightVehicles: [],
  updating: false,
  updateSuccess: false,
  entities: [],
  entity: {} as IKpiPresetWeight,
  totalItems: 0,
};

const apiUrl = 'api/kpi-preset-weight';

export const getEntitiesList = createAsyncThunk('kpiPresetWeight/fetch_entity_list', async () => {
  const requestUrl = `${apiUrl}-list`;
  return axios.get<IKpiPresetWeight[]>(requestUrl);
});

export const getEntitiesListByCompany = createAsyncThunk('kpiPresetWeight/fetch_entity_list_by_company', async (companyId: number | null) => {
  let requestUrl = `${apiUrl}-list`;
  if (companyId !== null && companyId !== undefined) {
    requestUrl += `?companyId=${companyId}`;
  }
  return axios.get<IKpiPresetWeight[]>(requestUrl);
});

export const createEntity = createAsyncThunk(
  'kpiPresetWeight/create_entity',
  async (data: { entity: IKpiPresetWeight; companyId?: number | null }, thunkAPI) => {
    const { entity, companyId } = data;

    const entityToSend = { ...entity };
    if (companyId) {
      entityToSend.company = { id: companyId };
    }

    const result = await axios.post<IKpiPresetWeight>(apiUrl, cleanEntity(entityToSend));

    if (companyId) {
      thunkAPI.dispatch(getEntitiesListByCompany(companyId));
    } else {
      thunkAPI.dispatch(getEntitiesList());
    }
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'kpiPresetWeight/update_entity',
  async (data: { entity: IKpiPresetWeight; companyId?: number | null }, thunkAPI) => {
    const { entity, companyId } = data;

    const entityToSend = { ...entity };
    if (companyId && !entityToSend.company) {
      entityToSend.company = { id: companyId };
    }

    const result = await axios.put<IKpiPresetWeight>(`${apiUrl}/${entity.id}`, cleanEntity(entityToSend));

    if (companyId) {
      thunkAPI.dispatch(getEntitiesListByCompany(companyId));
    } else {
      thunkAPI.dispatch(getEntitiesList());
    }
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'kpiPresetWeight/delete_entity',
  async (data: { id: string | number; companyId?: number | null }, thunkAPI) => {
    const { id, companyId } = data;
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IKpiPresetWeight>(requestUrl);

    if (companyId) {
      thunkAPI.dispatch(getEntitiesListByCompany(companyId));
    } else {
      thunkAPI.dispatch(getEntitiesList());
    }
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const KpiPresetWeightSlice = createEntitySlice({
  name: 'KpiPresetWeight',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntitiesList, getEntitiesListByCompany), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntitiesList, getEntitiesListByCompany), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = KpiPresetWeightSlice.actions;
export default KpiPresetWeightSlice.reducer;
